import { ChevronDown, ChevronRight } from 'lucide-react'
import { useMemo, useState } from 'react'

import { Button } from '@/components/ui/button'
import { Skeleton, SkeletonRows } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import {
  type AgentRow,
  type AgentRunParams,
  type RunMetrics,
  useAgentRunAgents,
} from '@/models/agentRunReport'
import { METRIC_COLUMNS } from './agentColumns'
import { AgentModelRows } from './AgentModelRows'

type SortKey = keyof RunMetrics

export function AgentsTab({ params }: { params: AgentRunParams }) {
  const { data: agents, isLoading } = useAgentRunAgents(params)
  const [sort, setSort] = useState<SortKey>('runs')
  const [expanded, setExpanded] = useState<Set<string>>(new Set())

  const rows = useMemo(() => {
    if (!agents) return []
    return [...agents].sort(
      (a, b) => (b[sort] ?? 0) - (a[sort] ?? 0) || a.agentSlug.localeCompare(b.agentSlug)
    )
  }, [agents, sort])

  const toggle = (slug: string) =>
    setExpanded((current) => {
      const next = new Set(current)
      if (next.has(slug)) next.delete(slug)
      else next.add(slug)
      return next
    })

  if (isLoading) {
    return (
      <div className="flex flex-col gap-3">
        <Skeleton className="h-10 w-full" />
        <SkeletonRows rows={8} />
      </div>
    )
  }

  if (!rows.length) return <p className="text-ink-400 py-12 text-center text-sm">No runs in this range.</p>

  return (
    <div className="flex flex-col gap-3">
      <p className="text-ink-400 text-sm">
        Expand an agent to compare the models it ran on — same job, same window.
      </p>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="font-semibold">Agent</TableHead>
            {METRIC_COLUMNS.map((column) => (
              <TableHead key={column.key} className="text-right">
                <Button
                  variant="ghost"
                  size="sm"
                  className={sort === column.key ? 'font-semibold' : 'text-ink-500 font-normal'}
                  onClick={() => setSort(column.key)}
                >
                  {column.label}
                </Button>
              </TableHead>
            ))}
          </TableRow>
        </TableHeader>
        <TableBody>
          {rows.map((agent) => (
            <AgentTableRow
              key={agent.agentSlug}
              agent={agent}
              expanded={expanded.has(agent.agentSlug)}
              onToggle={() => toggle(agent.agentSlug)}
            />
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

function AgentTableRow({
  agent,
  expanded,
  onToggle,
}: {
  agent: AgentRow
  expanded: boolean
  onToggle: () => void
}) {
  const canExpand = agent.models.length > 1
  return (
    <>
      <TableRow
        className={canExpand ? 'cursor-pointer' : undefined}
        onClick={canExpand ? onToggle : undefined}
      >
        <TableCell className="font-mono text-sm">
          <span className="flex items-center gap-1">
            {canExpand ? (
              expanded ? (
                <ChevronDown className="text-ink-400 h-3.5 w-3.5 shrink-0" />
              ) : (
                <ChevronRight className="text-ink-400 h-3.5 w-3.5 shrink-0" />
              )
            ) : (
              <span className="w-3.5 shrink-0" />
            )}
            {agent.agentSlug}
          </span>
        </TableCell>
        {METRIC_COLUMNS.map((column) => {
          const note = column.note?.(agent)
          return (
            <TableCell key={column.key} className="text-right tabular-nums">
              {column.render(agent)}
              {note && <div className="text-ink-400 text-xs">{note}</div>}
            </TableCell>
          )
        })}
      </TableRow>
      {expanded && <AgentModelRows models={agent.models} />}
    </>
  )
}
